import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

/**
 * BuilderShell — v3 two-panel builder layout (Apply Edge).
 *
 *   left   — the editor: Content forms OR Design settings (parent swaps by mode)
 *   right  — the live preview, ALWAYS visible (never replaced)
 *
 * Presentational. The preview is given the larger share and stays mounted across
 * mode switches so the artboard iframe never reloads. An optional AI tailoring
 * rail (`aiSlot`) opens as a right-edge drawer over the preview.
 */
export type BuilderShellProps = {
  /** The mode-switched editor column (Content or Design). */
  leftSlot: ReactNode;
  /** The live preview. Always rendered. */
  rightSlot: ReactNode;
  aiSlot?: ReactNode;
  aiOpen?: boolean;
  onAiClose?: () => void;
};

export const BuilderShell = ({
  leftSlot,
  rightSlot,
  aiSlot,
  aiOpen = false,
  onAiClose,
}: BuilderShellProps) => {
  return (
    <div
      data-testid="builder-shell"
      className="hidden h-full w-full grid-cols-[minmax(380px,460px)_1fr] overflow-hidden bg-background lg:grid"
    >
      {/* LEFT — editor (Content | Design) */}
      <aside className="flex h-full min-h-0 flex-col overflow-hidden border-r border-border">
        {leftSlot}
      </aside>

      {/* RIGHT — preview, always visible */}
      <main className="relative flex h-full min-h-0 flex-col overflow-hidden bg-muted/40">
        {rightSlot}

        {aiSlot ? (
          <>
            <div
              className={cn(
                "absolute inset-0 z-20 bg-zinc-950/40 transition-opacity",
                aiOpen ? "opacity-100" : "pointer-events-none opacity-0",
              )}
              onClick={onAiClose}
            />
            <aside
              className={cn(
                "absolute inset-y-0 right-0 z-30 w-[380px] max-w-[90%] border-l border-border bg-background shadow-lg transition-transform duration-200",
                aiOpen ? "translate-x-0" : "translate-x-full",
              )}
            >
              {aiSlot}
            </aside>
          </>
        ) : null}
      </main>
    </div>
  );
};
